import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useUserStore } from '../stores/userStore';
import { useChatStore } from '../store/chatStore';
import { authService } from '../services/authService';
import { Logo } from './layout/Logo';
import { PageHeader } from './layout/pageHeader';
import { LandingHeader } from './layout/LandingHeader';
import { Footer } from './layout/Footer';

export default function Auth() {
  const [error, setError] = useState<string | null>(null);
  const { user, isInitialized, checkSession } = useAuth();
  const { setUser } = useUserStore();
  const navigate = useNavigate();
  const location = useLocation();
  const { createChat } = useChatStore();

  // Handle authenticated user navigation
  useEffect(() => {
    if (isInitialized && user) {
      const returnTo = location.state?.from?.pathname || '/';
      const pendingMessage = sessionStorage.getItem('pendingMessage');

      const initializeSession = async () => {
        try {
          // Ensure WebSocket is connected when restoring session
          await authService.initializeWebSocket();
          
          if (pendingMessage) {
            try {
              const newChat = await createChat(pendingMessage, user.id);
              if (newChat) {
                await useChatStore.getState().sendMessage(
                  pendingMessage,
                  newChat.thread_id,
                  newChat.id
                );
                sessionStorage.removeItem('pendingMessage');
                navigate(`/chat/${newChat.id}`, { replace: true });
              }
            } catch (err) {
              console.error('Failed to create chat:', err);
              navigate(returnTo, { replace: true });
            }
          } else {
            navigate(returnTo, { replace: true });
          }
        } catch (error) {
          console.error('Failed to initialize session:', error);
          navigate(returnTo, { replace: true });
        }
      };

      initializeSession();
    }
  }, [user, isInitialized]);

  const handleTestLogin = async () => {
    setError(null);
    try {
      const response = await authService.login('hi@sorel.xyz', 'Futu10buric!');
      if (response.error) throw response.error;
      if (!response.user) throw new Error('Authentication failed: No user data');
      if (!response.session?.access_token) throw new Error('Authentication failed: No session data');

      setUser(response.user);
      await new Promise(resolve => setTimeout(resolve, 100));
      await checkSession();
      
      // Initialize WebSocket connection after successful authentication
      await authService.initializeWebSocket();
      
      navigate('/', { replace: true });
    } catch (error: any) {
      console.error('[Auth] Error:', error);
      setError(error.message || 'Authentication failed');
    }
  };

  const handleGoogleLogin = async () => {
    setError(null);
    try {
      await authService.signInWithGoogle();
      // The page will redirect to Google's OAuth page
    } catch (error: any) {
      console.error('[Auth] Google login error:', error);
      setError(error.message || 'Google authentication failed');
    }
  };

  // Show loading state only during initial auth check
  if (!isInitialized) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F1EAE1]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#361E06]" />
      </div>
    );
  }

  // If initialized and no user, show login form
  return (
    <div className="min-h-screen flex flex-col">
      <LandingHeader color="#361E06" />
      <div className="flex-grow flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl w-full space-y-8">
          <div className="text-center">
            <PageHeader
              headline="Connect to Theos"
              subheadline="Use one of the following options to continue"
            />
          </div>

          <div className="space-y-4 max-w-sm mx-auto">
            {error && (
              <div className="text-red-600 text-center">{error}</div>
            )}
            
            <button
              type="button"
              onClick={handleGoogleLogin}
              className="w-full flex items-center justify-center bg-white text-[#361E06] rounded-lg p-3 hover:bg-gray-50 border border-[#E5E7EB]"
            >
              <img src="/google-logo.svg" alt="Google" className="w-5 h-5 mr-2" />
              Connect with Google
            </button>

            <div className="text-center">
              <button
                onClick={handleTestLogin}
                className="text-[#361E06] hover:underline text-sm"
              >
                Test login
              </button>
            </div>
          </div>
        </div>
      </div>
      <Footer variant="auth" />
    </div>
  );
}